import React, { useState } from "react";
import { withMonogramContext } from "./MonogramContext";
import MonogramInitialsDrawer from "./MonogramInitialsDrawer";
import MonogramWordsDrawer from "./MonogramWordsDrawer";
import MonogramMultibutton from "./MonogramMultibutton";
import MonogramHeader from "./MonogramHeader";
import MonogramCalibrationDrawer from "./MonogramCalibrationDrawer";
import { DRAWER_NAMES, OVERLAYS } from "./const";
import Icon from "../minicart/icon";
import clsx from "clsx";
import { formatMoney } from "@shopify/theme-currency";
import MonogramWhatFits from "./MonogramWhatFIts";

function MonogramSideBar({
  drawer,
  setDrawer,
  product,
  handleContinue,
  errors,
  calibrating,
  language,
  data,
  overlay,
  setOverlay,
  handleCloseAttempt,
  chosenVariant,
  saveInProgress,
  addToCartInProgress,
  fontsPerDrawer,
  userActions,
  chosenFont,
}) {
  const [summaryShown, setSummaryShown] = useState(false);

  const getDrawerButtonClassName = (currentDrawer) =>
    drawer === currentDrawer
      ? "monogram-button monogram-button--primary"
      : "monogram-button monogram-button--primary monogram-button--primary-white";

  const isEditing = !!window.location.search.includes("timestamp");
  const isPreOrder =
    chosenVariant.available &&
    !!chosenVariant.inventory_threshold &&
    chosenVariant.inventory_quantity <= chosenVariant.inventory_threshold;
  const hasWhatFits = data?.description || data?.what_fits;

  const fee_product = chosenFont.monogram_fee_2x ? data.fee_product_2x : data.fee_product;

  return (
    <div className="monogram__sidebar">
      <MonogramHeader desktopOnly overlay={overlay} handleCloseAttempt={handleCloseAttempt} />

      {overlay === OVERLAYS.Exit ? (
        <div className="monogram__sidebar-settings">
          <p>{language.exit_intent_message}</p>
        </div>
      ) : overlay === OVERLAYS.WhatFits ? (
        <div className="monogram__sidebar-settings">
          <MonogramWhatFits data={data} />
        </div>
      ) : (
        <div className="monogram__sidebar-settings">
          <div className="monogram__sidebar-settings__title-container">
            <h1 className="monogram__sidebar-settings__title">{product.title}</h1>
            {hasWhatFits && (
              <button
                type="button"
                onClick={() => setOverlay(OVERLAYS.WhatFits)}
                className="monogram__sidebar-what-fits"
              >
                <span>
                  <Icon id="ruler" />
                </span>
                What Fits?
              </button>
            )}
            {data.measurements && (
              <p className="monogram__sidebar-settings__subtitle">
                <span>Measurements</span>
                <span>{data.measurements}</span>
              </p>
            )}
          </div>

          <MonogramMultibutton
            firstButton={{
              children: language.words_drawer_title,
              onClick: () => setDrawer(DRAWER_NAMES.Words),
              className: getDrawerButtonClassName(DRAWER_NAMES.Words),
            }}
            secondButton={{
              children: language.letters_drawer_title,
              onClick: () => setDrawer(DRAWER_NAMES.Initials),
              className: getDrawerButtonClassName(DRAWER_NAMES.Initials),
            }}
            fontsPerDrawer={fontsPerDrawer}
          />
          <div className="monogram__sidebar-disclaimer monogram__sidebar-disclaimer--top">
            {data?.sidebar_footer_richtext && (
              <div
                className="monogram__sidebar-disclaimer__rte"
                dangerouslySetInnerHTML={{ __html: data?.sidebar_footer_richtext }}
              />
            )}
          </div>

          {calibrating && (
            <button
              className={getDrawerButtonClassName(DRAWER_NAMES.Calibration)}
              style={{ marginTop: "1rem" }}
              onClick={() => setDrawer(DRAWER_NAMES.Calibration)}
            >
              Calibrate
            </button>
          )}
          {drawer === DRAWER_NAMES.Initials && <MonogramInitialsDrawer />}
          {drawer === DRAWER_NAMES.Words && <MonogramWordsDrawer />}
          {drawer === DRAWER_NAMES.Calibration && <MonogramCalibrationDrawer />}

          <div className="monogram__sidebar-disclaimer">
            {drawer !== DRAWER_NAMES.Calibration && <p>{language.approximate_explainer_text}</p>}

            {data?.sidebar_footer_richtext && (
              <div
                className="monogram__sidebar-disclaimer__rte"
                dangerouslySetInnerHTML={{ __html: data?.sidebar_footer_richtext }}
              />
            )}
          </div>

          {isPreOrder && (
            <div className="monogram__sidebar-preorder-message">
              {chosenVariant.preorder_message}
            </div>
          )}
        </div>
      )}

      {drawer !== DRAWER_NAMES.Calibration && (
        <div className="monogram__sidebar-footer">
          {!!Object.keys(errors).length && (
            <ul className="monogram__sidebar-footer__errors">
              {Object.keys(errors).map((error) => (
                <li key={error}>{errors[error]}</li>
              ))}
            </ul>
          )}
          <div className="monogram__sidebar-footer__content">
            {overlay === OVERLAYS.Exit ? (
              <div className="monogram__exit-intent__buttons">
                <button
                  className="monogram-button uppercase"
                  onClick={() => setOverlay(OVERLAYS.Monogram)}
                >
                  Nevermind
                </button>
                <button
                  className="monogram-button uppercase monogram-button--primary"
                  onClick={handleCloseAttempt}
                >
                  Exit
                </button>
              </div>
            ) : overlay === OVERLAYS.WhatFits ? (
              <div className="monogram__exit-intent__buttons">
                <button
                  className="monogram-button uppercase monogram-button--primary"
                  onClick={() => setOverlay(OVERLAYS.Monogram)}
                >
                  Back to designs
                </button>
              </div>
            ) : !chosenVariant?.available ? (
              <div className="monogram-button monogram-button--primary monogram-button--primary-oos">
                <span>Out of Stock</span>
              </div>
            ) : (
              <div
                className={clsx("monogram__sidebar-footer-buttons", {
                  "monogram__sidebar-footer-button--summary-shown": summaryShown,
                })}
              >
                <div className="monogram__summary">
                  <button type="button" onClick={() => setSummaryShown(false)}>
                    <Icon id="x" />
                  </button>
                  <div className="monogram__summary-item">
                    <span>
                      {product.title} - {chosenVariant.title}
                    </span>
                    <span>{formatMoney(chosenVariant.price)}</span>
                  </div>
                  <div className="monogram__summary-item">
                    <span>Monogram Fee</span>
                    <span>{formatMoney(fee_product?.price)}</span>
                  </div>
                  <div className="monogram__summary-subtotal">
                    <span>Subtotal</span>
                    <span>{formatMoney(chosenVariant.price + fee_product?.price)}</span>
                  </div>
                </div>
                <div className="monogram-multibutton">
                  <button
                    type="button"
                    onClick={() => setSummaryShown(!summaryShown)}
                    className="monogram-button monogram-summary-button uppercase"
                  >
                    <span>Summary</span>
                    <Icon id="selectDownArrow" />
                  </button>
                  <button
                    type="button"
                    className={clsx("monogram-button monogram-button--primary", {
                      "monogram-button--disabled": Object.values(userActions).some((a) => !a),
                    })}
                    disabled={Object.values(userActions).some((a) => !a)}
                    onClick={handleContinue}
                  >
                    {chosenVariant?.is_coming_soon ? (
                      <span>{saveInProgress ? "Saving..." : "Save For Later"}</span>
                    ) : isEditing ? (
                      <span>{addToCartInProgress ? "Updating..." : "Update"}</span>
                    ) : (
                      <span>{addToCartInProgress ? "Adding..." : "Add To Bag"}</span>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default withMonogramContext(MonogramSideBar);
